<div class="w-full max-w-6xl mx-auto p-6">
  @if (error()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-red-600"></i>
      <p class="text-red-800">{{ error() }}</p>
    </div>
  }

  <div class="relative">
    @if (formDataLoading()) {
      <div class="absolute inset-0 z-40 flex items-start justify-center rounded-xl bg-slate-400/25 backdrop-blur-[2px] pt-24" role="status" aria-busy="true">
        <div class="flex flex-col items-center gap-3 rounded-2xl border border-slate-300/80 bg-white/95 px-8 py-6 shadow-lg">
          <i class="pi pi-spin pi-spinner text-3xl text-rose-600"></i>
          <p class="text-sm font-semibold text-slate-800">Cargando plantilla…</p>
        </div>
      </div>
    }

    <form [formGroup]="templateForm" (ngSubmit)="onSubmit()">
      <p-card styleClass="sale-form-card">
        <ng-template pTemplate="header">
          <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/50">
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-3 min-w-0">
                <p-button icon="pi pi-arrow-left" [rounded]="true" [text]="true" severity="secondary" type="button" (onClick)="cancel()" pTooltip="Volver al listado" tooltipPosition="bottom" />
                <h2 class="text-lg font-semibold text-slate-900 flex items-center gap-2 min-w-0">
                  <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
                    <i class="pi pi-envelope"></i>
                  </span>
                  <span class="truncate">{{ isEditMode() ? 'Editar plantilla' : 'Nueva plantilla' }}</span>
                </h2>
              </div>
              <div class="flex items-center gap-2">
                <p-button label="Vista previa avanzada" icon="pi pi-eye" severity="secondary" [outlined]="true" size="small" type="button" (onClick)="openPreviewDrawer()" />
              </div>
            </div>
          </div>
        </ng-template>

        <div class="mt-4 space-y-6">
          <div>
            <h3 class="text-lg font-semibold text-slate-900">Configuración</h3>
            <p class="text-sm text-slate-500">Tipo de comunicado y nombre interno.</p>
          </div>

          <div class="space-y-4">
            <p-floatlabel class="block w-full md:w-1/4" variant="on">
              <p-select formControlName="type" inputId="commType" [options]="typeOptions" optionLabel="label" optionValue="value" [showClear]="true" class="w-full" />
              <label for="commType">Tipo</label>
            </p-floatlabel>
            <p-floatlabel class="w-full" variant="on">
              <input pInputText id="commName" formControlName="name" class="w-full" autocomplete="off" />
              <label for="commName">Nombre</label>
            </p-floatlabel>
          </div>

          <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/80">
              <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Mensaje</p>
              <p-floatlabel class="w-full" variant="on">
                <input pInputText id="commSubject" formControlName="subject" class="w-full font-medium" autocomplete="off" />
                <label for="commSubject">Asunto</label>
              </p-floatlabel>
            </div>

            <div class="message-composer-body">
              <div class="message-composer-inner">
                <div class="message-editor-shell message-editor-shell--with-vars" [class.html-mode]="editorHtmlMode()">
                  @if (mentionReady()) {
                    <p-editor
                      formControlName="messageBody"
                      [modules]="quillModules"
                      [style]="editorStyle"
                      (onInit)="onEditorInit($event)"
                    >
                      <ng-template pTemplate="header">
                        <span class="ql-formats">
                          <select class="ql-font" aria-label="Tipo de fuente"></select>
                          <select class="ql-header" aria-label="Encabezado"></select>
                        </span>
                        <span class="ql-formats">
                          <button type="button" class="ql-bold" aria-label="Negrita"></button>
                          <button type="button" class="ql-italic" aria-label="Cursiva"></button>
                          <button type="button" class="ql-underline" aria-label="Subrayado"></button>
                        </span>
                        <span class="ql-formats">
                          <select class="ql-color" aria-label="Color de texto"></select>
                          <select class="ql-background" aria-label="Color de fondo"></select>
                        </span>
                        <span class="ql-formats">
                          <button type="button" class="ql-list" [attr.value]="'ordered'" aria-label="Lista numerada"></button>
                        </span>
                        <span class="ql-formats">
                          <select class="ql-align" aria-label="Alineación"></select>
                        </span>
                        <span class="ql-formats pento-html-source-formats">
                          <button
                            type="button"
                            class="pento-html-source-toggle"
                            [class.ql-active]="editorHtmlMode()"
                            aria-label="Modo HTML"
                            title="Modo HTML"
                            (click)="toggleEditorHtmlMode($event)"
                          >&lt;&gt;</button>
                        </span>
                      </ng-template>
                    </p-editor>
                    @if (editorHtmlMode()) {
                      <textarea
                        #messageBodyHtmlSource
                        class="editor-html-source"
                        spellcheck="false"
                        [(ngModel)]="editorHtmlSource"
                        [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="onEditorHtmlSourceChange()"
                      ></textarea>
                    }
                  } @else {
                    <div class="message-editor-loading flex items-center justify-center text-slate-500 gap-2">
                      <i class="pi pi-spin pi-spinner"></i>
                      <span>Cargando editor…</span>
                    </div>
                  }
                </div>

                <aside class="message-vars-panel">
                  <div class="message-vars-header">
                    <h4 class="text-sm font-semibold text-slate-800">Variables</h4>
                  </div>
                  <div class="message-vars-list">
                    @if (availableVars().length === 0) {
                      <p class="px-2 py-4 text-xs text-slate-500 text-center">Selecciona un tipo para ver variables.</p>
                    } @else {
                      @for (variable of availableVars(); track variable.id) {
                        <button
                          type="button"
                          class="message-var-item"
                          (click)="insertVariable(variable.varKey!)"
                          [pTooltip]="variable.description || variable.label"
                          tooltipPosition="left"
                        >
                          <span class="text-xs font-medium text-slate-700 leading-snug">{{ variable.label }}</span>
                          <span class="font-mono text-xs text-sky-700">@{{ variable.varKey }}</span>
                        </button>
                      }
                    }
                  </div>
                </aside>
              </div>
            </div>
          </div>

          <div class="pt-2">
            <div class="flex items-center justify-between mb-4 gap-3">
              <div>
                <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <i class="pi pi-paperclip"></i>
                  Adjuntos
                </h3>
                <p class="text-sm text-slate-500">
                  @if (isEditMode()) {
                    Archivos de la plantilla (máximo 10 MB). Se envían con cada correo que use esta plantilla.
                  } @else {
                    Puedes agregar archivos ahora; se subirán al guardar la plantilla (máximo 10 MB).
                  }
                </p>
              </div>
              <div>
                <input #attachmentInput type="file" class="hidden" multiple (change)="onAttachmentSelect($event)" />
                <p-button
                  type="button"
                  [label]="uploadingAttachments() ? 'Subiendo...' : 'Subir archivo'"
                  [icon]="uploadingAttachments() ? 'pi pi-spin pi-spinner' : 'pi pi-upload'"
                  [loading]="uploadingAttachments()"
                  [disabled]="uploadingAttachments() || loading()"
                  (onClick)="attachmentInput.click()"
                />
              </div>
            </div>
            <div class="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Archivo</th>
                      <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider hidden sm:table-cell">Tipo</th>
                      <th class="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Tamaño</th>
                      <th class="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider hidden sm:table-cell">Fecha</th>
                      <th class="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-slate-200">
                    @if (attachmentsLoading()) {
                      <tr>
                        <td class="px-4 py-8 text-center text-sm text-slate-500" colspan="5">
                          <i class="pi pi-spin pi-spinner mr-2"></i>Cargando adjuntos...
                        </td>
                      </tr>
                    } @else if (attachments().length === 0 && pendingAttachments().length === 0) {
                      <tr>
                        <td class="px-4 py-6 text-center text-sm text-slate-500" colspan="5">No hay archivos adjuntos.</td>
                      </tr>
                    } @else {
                      @for (file of attachments(); track file.id) {
                        <tr class="hover:bg-slate-50 transition-colors">
                          <td class="px-4 py-3">
                            <span class="flex items-center gap-2 text-sm font-medium text-slate-900">
                              <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                                <i [class]="'pi ' + getFileIcon(file.contentType)"></i>
                              </span>
                              <span class="truncate max-w-xs">{{ file.name }}</span>
                            </span>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-600 hidden sm:table-cell">{{ file.contentType || '-' }}</td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-600 text-right">{{ formatFileSize(file.size) }}</td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-600 text-center hidden sm:table-cell">
                            {{ file.createdAt ? (file.createdAt | date:'dd/MM/yyyy HH:mm') : '-' }}
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-right">
                            <p-button icon="pi pi-download" [rounded]="true" [text]="true" severity="secondary" styleClass="hover:!bg-rose-100" (onClick)="openAttachment(file)" />
                            <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger" (onClick)="confirmDeleteAttachment(file)" />
                          </td>
                        </tr>
                      }
                      @for (pending of pendingAttachments(); track pending.key) {
                        <tr class="hover:bg-slate-50 transition-colors">
                          <td class="px-4 py-3">
                            <span class="flex items-center gap-2 text-sm font-medium text-slate-900">
                              <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                                <i [class]="'pi ' + getFileIcon(pending.file.type)"></i>
                              </span>
                              <span class="min-w-0">
                                <span class="truncate max-w-xs block">{{ pending.file.name }}</span>
                                <span class="text-[11px] font-normal text-amber-700">Pendiente de subir</span>
                              </span>
                            </span>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-600 hidden sm:table-cell">{{ pending.file.type || '-' }}</td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-600 text-right">{{ formatFileSize(pending.file.size) }}</td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-600 text-center hidden sm:table-cell">—</td>
                          <td class="px-4 py-3 whitespace-nowrap text-right">
                            <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger" (onClick)="removePendingAttachment(pending.key)" />
                          </td>
                        </tr>
                      }
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <ng-template pTemplate="footer">
          <div class="!mt-4 flex justify-end gap-4 mt-1 !p-4 !-m-5 bg-gray-50 p-2 border-t border-slate-200">
            <p-button label="Cancelar" severity="secondary" (onClick)="cancel()" [loading]="loading()" type="button" />
            <p-button
              [label]="loading() ? 'Guardando...' : (isEditMode() ? 'Actualizar plantilla' : 'Guardar plantilla')"
              icon="pi pi-save"
              [loading]="loading()"
              [disabled]="templateForm.invalid || loading()"
              type="submit"
            />
          </div>
        </ng-template>
      </p-card>
    </form>
  </div>
</div>

<p-drawer
  [visible]="previewDrawerOpen()"
  (visibleChange)="onPreviewDrawerVisibleChange($event)"
  position="right"
  styleClass="!w-full sm:!w-[32rem] lg:!w-[40rem]"
  header="Vista previa del mensaje"
>
  <div class="space-y-4 min-w-0 overflow-x-hidden">
    <p class="text-sm text-slate-600">Ajusta los valores de prueba para ver cómo se verá el mensaje con las variables reemplazadas.</p>

    <div class="space-y-3">
      @for (variable of availableVars(); track variable.id) {
        <p-floatlabel class="w-full" variant="on">
          <input
            pInputText
            [id]="'previewVar_' + variable.varKey"
            class="w-full p-inputtext-sm"
            [(ngModel)]="previewVariables[variable.varKey!]"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onPreviewVariableChange()"
          />
          <label [for]="'previewVar_' + variable.varKey">{{ variable.label }} (@{{ variable.varKey }})</label>
        </p-floatlabel>
      }
    </div>

    <div class="flex justify-end">
      <p-button label="Actualizar vista previa" icon="pi pi-refresh" size="small" (onClick)="refreshPreview()" />
    </div>

    <div class="email-preview-frame">
      <div class="email-preview-header">
        <p class="text-xs text-slate-500 mb-1">Asunto</p>
        <p class="text-sm font-semibold text-slate-900 email-preview-subject">{{ previewSubject() || '(sin asunto)' }}</p>
      </div>
      <div class="email-preview-body" [innerHTML]="previewBody()"></div>
    </div>
  </div>
</p-drawer>
